import React, { useEffect, useState } from "react";
import { Card, Table, Button, Form, Badge, Modal, Row, Col, Tabs, Tab } from "react-bootstrap";
import {
  getAllColleges,
  addCollege,
  updateCollege,
  deleteCollege,
  assignStudentToCollege,
  removeStudentFromCollege,
  updateStudentStatusInCollege,
  getCollegeStatistics,
  bulkAssignStudentsToCollege,
  COLLEGE_STATUS,
  INTERNSHIP_TYPES,
  AGREEMENT_TYPES,
} from "../services/collegeService";
import { getStudents } from "../services/userService";
import Loader from "../components/Loader";
import { STUDENT_LEVELS } from "../utils";
import { toast } from "react-toastify";
import { MapPin, Users, Calendar, FileText, Building } from "lucide-react";

export default function Colleges() {
  const [colleges, setColleges] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("colleges");

  // Modal states
  const [showCollegeForm, setShowCollegeForm] = useState(false);
  const [showStudentAssignment, setShowStudentAssignment] = useState(false);
  const [showBulkAssignment, setShowBulkAssignment] = useState(false);
  const [editingCollege, setEditingCollege] = useState(null);
  const [selectedCollege, setSelectedCollege] = useState(null);

  // Form states
  const [collegeFormData, setCollegeFormData] = useState({
    name: "",
    location: "",
    contactPerson: "",
    contactEmail: "",
    contactPhone: "",
    website: "",
    agreementType: AGREEMENT_TYPES.MOU,
    agreementStartDate: "",
    agreementEndDate: "",
    internshipTypes: [],
    description: "",
    capacity: "",
  });

  const [studentAssignmentData, setStudentAssignmentData] = useState({
    userId: "",
    userName: "",
    userEmail: "",
    internshipType: INTERNSHIP_TYPES.FULL_TIME,
    startDate: "",
    endDate: "",
    stipend: "",
    notes: "",
  });

  const [selectedStudentsForBulk, setSelectedStudentsForBulk] = useState([]);

  // Filters
  const [statusFilter, setStatusFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === "statistics") {
      fetchStatistics();
    }
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [collegesData, studentsData] = await Promise.all([
        getAllColleges(),
        getStudents(),
      ]);

      setColleges(collegesData);
      setAllStudents(studentsData.filter(student => student.isActive));
    } catch (error) {
      toast.error("Failed to fetch data");
      console.error(error);
    }
    setLoading(false);
  };

  const fetchStatistics = async () => {
    try {
      const stats = await getCollegeStatistics();
      setStatistics(stats);
    } catch (error) {
      toast.error("Failed to fetch statistics");
      console.error(error);
    }
  };

  const handleCollegeFormSubmit = async (e) => {
    e.preventDefault();

    if (!collegeFormData.name || !collegeFormData.contactPerson) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const formData = {
        ...collegeFormData,
        capacity: parseInt(collegeFormData.capacity) || 0,
        agreementStartDate: collegeFormData.agreementStartDate
          ? new Date(collegeFormData.agreementStartDate)
          : null,
        agreementEndDate: collegeFormData.agreementEndDate
          ? new Date(collegeFormData.agreementEndDate)
          : null,
      };

      if (editingCollege) {
        await updateCollege(editingCollege.id, formData);
      } else {
        await addCollege(formData);
      }

      fetchData();
      resetCollegeForm();
      setShowCollegeForm(false);
    } catch (error) {
      console.error("Error saving college:", error);
    }
  };

  const handleDeleteCollege = async (collegeId) => {
    if (window.confirm("Are you sure you want to delete this college?")) {
      try {
        await deleteCollege(collegeId);
        fetchData();
      } catch (error) {
        console.error("Error deleting college:", error);
      }
    }
  };

  const handleStudentAssignment = async (e) => {
    e.preventDefault();

    if (!studentAssignmentData.userId || !selectedCollege) {
      toast.error("Please select a student and ensure college is selected");
      return;
    }

    try {
      const assignmentData = {
        ...studentAssignmentData,
        startDate: studentAssignmentData.startDate
          ? new Date(studentAssignmentData.startDate)
          : null,
        endDate: studentAssignmentData.endDate
          ? new Date(studentAssignmentData.endDate)
          : null,
        stipend: parseFloat(studentAssignmentData.stipend) || 0,
      };

      await assignStudentToCollege(selectedCollege.id, assignmentData);
      fetchData();
      resetStudentAssignmentForm();
      setShowStudentAssignment(false);
    } catch (error) {
      console.error("Error assigning student:", error);
    }
  };

  const handleBulkAssignment = async () => {
    if (selectedStudentsForBulk.length === 0 || !selectedCollege) {
      toast.error("Please select students and ensure college is selected");
      return;
    }

    try {
      const studentsData = selectedStudentsForBulk.map(studentId => {
        const student = allStudents.find(s => s.id === studentId);
        return {
          userId: student.id,
          userName: student.name,
          userEmail: student.email,
          internshipType: INTERNSHIP_TYPES.FULL_TIME,
          startDate: new Date(),
          stipend: 0,
          notes: "Bulk assignment",
        };
      });

      await bulkAssignStudentsToCollege(selectedCollege.id, studentsData);
      fetchData();
      setSelectedStudentsForBulk([]);
      setShowBulkAssignment(false);
    } catch (error) {
      console.error("Error bulk assigning students:", error);
    }
  };

  const resetCollegeForm = () => {
    setCollegeFormData({
      name: "",
      location: "",
      contactPerson: "",
      contactEmail: "",
      contactPhone: "",
      website: "",
      agreementType: AGREEMENT_TYPES.MOU,
      agreementStartDate: "",
      agreementEndDate: "",
      internshipTypes: [],
      description: "",
      capacity: "",
    });
    setEditingCollege(null);
  };

  const resetStudentAssignmentForm = () => {
    setStudentAssignmentData({
      userId: "",
      userName: "",
      userEmail: "",
      internshipType: INTERNSHIP_TYPES.FULL_TIME,
      startDate: "",
      endDate: "",
      stipend: "",
      notes: "",
    });
  };

  const handleEditCollege = (college) => {
    setEditingCollege(college);
    setCollegeFormData({
      name: college.name || "",
      location: college.location || "",
      contactPerson: college.contactPerson || "",
      contactEmail: college.contactEmail || "",
      contactPhone: college.contactPhone || "",
      website: college.website || "",
      agreementType: college.agreementType || AGREEMENT_TYPES.MOU,
      agreementStartDate: college.agreementStartDate 
        ? college.agreementStartDate.toISOString().split('T')[0] 
        : "",
      agreementEndDate: college.agreementEndDate 
        ? college.agreementEndDate.toISOString().split('T')[0] 
        : "",
      internshipTypes: college.internshipTypes || [],
      description: college.description || "",
      capacity: college.capacity?.toString() || "",
    });
    setShowCollegeForm(true);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === "checkbox") {
      if (name === "internshipTypes") {
        setCollegeFormData(prev => ({
          ...prev,
          internshipTypes: checked 
            ? [...prev.internshipTypes, value]
            : prev.internshipTypes.filter(type => type !== value)
        }));
      }
    } else {
      setCollegeFormData(prev => ({
        ...prev,
        [name]: value
      }));

      // Auto-fill student data when student is selected
      if (name === "userId" && value) {
        const selectedStudent = allStudents.find(s => s.id === value);
        if (selectedStudent) {
          setStudentAssignmentData(prev => ({
            ...prev,
            userName: selectedStudent.name,
            userEmail: selectedStudent.email,
          }));
        }
      }
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      [COLLEGE_STATUS.ACTIVE]: "success",
      [COLLEGE_STATUS.INACTIVE]: "secondary",
      [COLLEGE_STATUS.PENDING]: "warning",
      [COLLEGE_STATUS.SUSPENDED]: "danger",
    };

    return <Badge bg={statusColors[status] || "secondary"}>{status?.toUpperCase()}</Badge>;
  };

  const getAgreementTypeBadge = (type) => {
    const typeColors = {
      [AGREEMENT_TYPES.MOU]: "primary",
      [AGREEMENT_TYPES.CONTRACT]: "success",
      [AGREEMENT_TYPES.INFORMAL]: "warning",
      [AGREEMENT_TYPES.PARTNERSHIP]: "info",
    };

    return <Badge bg={typeColors[type] || "secondary"}>{type?.toUpperCase()}</Badge>;
  };

  const filteredColleges = colleges.filter(college => {
    const matchesStatus = !statusFilter || college.status === statusFilter;
    const matchesSearch = !searchTerm || 
      college.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      college.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      college.contactPerson?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesStatus && matchesSearch;
  });

  return (
    <div className="p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="text-2xl font-bold">College Management</h2>
        <div className="d-flex gap-2">
          <Button
            variant="primary"
            onClick={() => setShowCollegeForm(true)}
            disabled={loading}
          >
            <Building size={16} className="me-2" />
            Add College
          </Button>
        </div>
      </div>

      {loading && <Loader />}

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
        <Tab eventKey="colleges" title="Colleges">
          <Card className="shadow">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <Card.Title>College Partnerships</Card.Title>
                <div className="d-flex gap-2">
                  <Form.Control
                    type="text"
                    placeholder="Search colleges..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ width: "200px" }}
                  />
                  <Form.Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    style={{ width: "150px" }}
                  >
                    <option value="">All Status</option>
                    {Object.values(COLLEGE_STATUS).map((status) => (
                      <option key={status} value={status}>
                        {status.toUpperCase()}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              </div>

              <Table responsive hover>
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>College Name</th>
                    <th>Location</th>
                    <th>Contact Person</th>
                    <th>Agreement</th>
                    <th>Students</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredColleges.length === 0 && !loading ? (
                    <tr>
                      <td colSpan="8" className="text-center">
                        No colleges found
                      </td>
                    </tr>
                  ) : (
                    filteredColleges.map((college, index) => (
                      <tr key={college.id}>
                        <td>{index + 1}</td>
                        <td>
                          <div>
                            <strong>{college.name}</strong>
                            {college.website && (
                              <div>
                                <small>
                                  <a 
                                    href={college.website} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-decoration-none"
                                  >
                                    🌐 Website
                                  </a>
                                </small>
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            <MapPin size={14} className="me-1 text-muted" />
                            {college.location || "N/A"}
                          </div>
                        </td>
                        <td>
                          <div>
                            <strong>{college.contactPerson}</strong>
                            {college.contactEmail && (
                              <div><small>{college.contactEmail}</small></div>
                            )}
                            {college.contactPhone && (
                              <div><small>{college.contactPhone}</small></div>
                            )}
                          </div>
                        </td>
                        <td>
                          <div>
                            {getAgreementTypeBadge(college.agreementType)}
                            {college.agreementEndDate && (
                              <div>
                                <small className="text-muted">
                                  <Calendar size={12} className="me-1" />
                                  Until {college.agreementEndDate.toLocaleDateString()}
                                </small>
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            <Users size={14} className="me-1 text-primary" />
                            <span>{college.studentsCount || 0}</span>
                            {college.capacity && (
                              <small className="text-muted ms-1">/ {college.capacity}</small>
                            )}
                          </div>
                        </td>
                        <td>{getStatusBadge(college.status)}</td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleEditCollege(college)}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="outline-success"
                              size="sm"
                              onClick={() => {
                                setSelectedCollege(college);
                                setShowStudentAssignment(true);
                              }}
                            >
                              Assign
                            </Button>
                            <Button
                              variant="outline-info"
                              size="sm"
                              onClick={() => {
                                setSelectedCollege(college);
                                setShowBulkAssignment(true);
                              }}
                            >
                              Bulk
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteCollege(college.id)}
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="statistics" title="Statistics">
          <Row>
            <Col md={12}>
              {statistics && (
                <>
                  <Row className="mb-4">
                    <Col md={3}>
                      <Card className="text-center mb-3">
                        <Card.Body>
                          <h5 className="text-primary">{statistics.totalColleges}</h5>
                          <small>Total Colleges</small>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={3}>
                      <Card className="text-center mb-3">
                        <Card.Body>
                          <h5 className="text-success">{statistics.activeColleges}</h5>
                          <small>Active Partnerships</small>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={3}>
                      <Card className="text-center mb-3">
                        <Card.Body>
                          <h5 className="text-info">{statistics.totalStudentsAssigned}</h5>
                          <small>Students Assigned</small>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={3}>
                      <Card className="text-center mb-3">
                        <Card.Body>
                          <h5 className="text-warning">{statistics.averageStudentsPerCollege}</h5>
                          <small>Avg Students/College</small>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Card>
                        <Card.Body>
                          <Card.Title>Agreement Types</Card.Title>
                          {Object.entries(statistics.collegesByAgreementType).map(([type, count]) => (
                            <div key={type} className="d-flex justify-content-between mb-2">
                              <span>{type.replace('_', ' ').toUpperCase()}</span>
                              <Badge bg="primary">{count}</Badge>
                            </div>
                          ))}
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={6}>
                      <Card>
                        <Card.Body>
                          <Card.Title>Internship Types</Card.Title>
                          {Object.entries(statistics.collegesByInternshipType).map(([type, count]) => (
                            <div key={type} className="d-flex justify-content-between mb-2">
                              <span>{type.replace('_', ' ').toUpperCase()}</span>
                              <Badge bg="info">{count}</Badge>
                            </div>
                          ))}
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </>
              )}
            </Col>
          </Row>
        </Tab>
      </Tabs>

      {/* College Form Modal */}
      <Modal show={showCollegeForm} onHide={() => setShowCollegeForm(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingCollege ? "Edit College" : "Add New College"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCollegeFormSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>College Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={collegeFormData.name}
                    onChange={handleFormChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Location</Form.Label>
                  <Form.Control
                    type="text"
                    name="location"
                    value={collegeFormData.location}
                    onChange={handleFormChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Contact Person *</Form.Label>
                  <Form.Control
                    type="text"
                    name="contactPerson"
                    value={collegeFormData.contactPerson}
                    onChange={handleFormChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Contact Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="contactEmail"
                    value={collegeFormData.contactEmail}
                    onChange={handleFormChange}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Contact Phone</Form.Label>
                  <Form.Control
                    type="tel"
                    name="contactPhone"
                    value={collegeFormData.contactPhone}
                    onChange={handleFormChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Website</Form.Label>
                  <Form.Control
                    type="url"
                    name="website"
                    value={collegeFormData.website}
                    onChange={handleFormChange}
                    placeholder="https://example.com"
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Agreement Type</Form.Label>
                  <Form.Select
                    name="agreementType"
                    value={collegeFormData.agreementType}
                    onChange={handleFormChange}
                  >
                    {Object.values(AGREEMENT_TYPES).map((type) => (
                      <option key={type} value={type}>
                        {type.replace('_', ' ').toUpperCase()}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Capacity</Form.Label>
                  <Form.Control
                    type="number"
                    name="capacity"
                    value={collegeFormData.capacity}
                    onChange={handleFormChange}
                    min="0"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Agreement Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="agreementStartDate"
                    value={collegeFormData.agreementStartDate}
                    onChange={handleFormChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Agreement End Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="agreementEndDate"
                    value={collegeFormData.agreementEndDate}
                    onChange={handleFormChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Internship Types</Form.Label>
              <div>
                {Object.values(INTERNSHIP_TYPES).map((type) => (
                  <Form.Check
                    key={type}
                    inline
                    type="checkbox"
                    name="internshipTypes"
                    value={type}
                    label={type.replace('_', ' ').toUpperCase()}
                    checked={collegeFormData.internshipTypes.includes(type)}
                    onChange={handleFormChange}
                  />
                ))}
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={collegeFormData.description}
                onChange={handleFormChange}
                placeholder="Brief description about the college and partnership..."
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCollegeForm(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary">
              {editingCollege ? "Update College" : "Add College"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Student Assignment Modal */}
      <Modal show={showStudentAssignment} onHide={() => setShowStudentAssignment(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Assign Student to {selectedCollege?.name}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleStudentAssignment}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Select Student *</Form.Label>
              <Form.Select
                name="userId"
                value={studentAssignmentData.userId}
                onChange={(e) => {
                  handleFormChange(e);
                  setStudentAssignmentData(prev => ({ ...prev, userId: e.target.value }));
                }}
                required
              >
                <option value="">Choose a student...</option>
                {allStudents.map((student) => (
                  <option key={student.id} value={student.id}>
                    {student.name} ({student.email}) - {student.studentLevel?.replace('_', ' ').toUpperCase()}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Internship Type</Form.Label>
                  <Form.Select
                    name="internshipType"
                    value={studentAssignmentData.internshipType}
                    onChange={(e) => setStudentAssignmentData(prev => ({ ...prev, internshipType: e.target.value }))}
                  >
                    {Object.values(INTERNSHIP_TYPES).map((type) => (
                      <option key={type} value={type}>
                        {type.replace('_', ' ').toUpperCase()}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Stipend (₹)</Form.Label>
                  <Form.Control
                    type="number"
                    name="stipend"
                    value={studentAssignmentData.stipend}
                    onChange={(e) => setStudentAssignmentData(prev => ({ ...prev, stipend: e.target.value }))}
                    min="0"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="startDate"
                    value={studentAssignmentData.startDate}
                    onChange={(e) => setStudentAssignmentData(prev => ({ ...prev, startDate: e.target.value }))}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>End Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="endDate"
                    value={studentAssignmentData.endDate}
                    onChange={(e) => setStudentAssignmentData(prev => ({ ...prev, endDate: e.target.value }))}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="notes"
                value={studentAssignmentData.notes}
                onChange={(e) => setStudentAssignmentData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Additional notes about the assignment..."
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowStudentAssignment(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="success">
              Assign Student
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Bulk Assignment Modal */}
      <Modal show={showBulkAssignment} onHide={() => setShowBulkAssignment(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Bulk Assign Students to {selectedCollege?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <strong>Select Students:</strong>
            <div style={{ maxHeight: "300px", overflowY: "auto" }} className="border rounded p-2 mt-2">
              {allStudents.map((student) => (
                <Form.Check
                  key={student.id}
                  type="checkbox"
                  label={`${student.name} (${student.email}) - ${student.studentLevel?.replace('_', ' ').toUpperCase()}`}
                  checked={selectedStudentsForBulk.includes(student.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedStudentsForBulk(prev => [...prev, student.id]);
                    } else {
                      setSelectedStudentsForBulk(prev => prev.filter(id => id !== student.id));
                    }
                  }}
                />
              ))}
            </div>
          </div>
          <div className="text-muted">
            <small>Selected: {selectedStudentsForBulk.length} students</small>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBulkAssignment(false)}>
            Cancel
          </Button>
          <Button 
            variant="success" 
            onClick={handleBulkAssignment}
            disabled={selectedStudentsForBulk.length === 0}
          >
            Assign Selected Students
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}